import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Stack,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { gmailExpensesService, Bill } from '../services/gmailExpenses';

const BillVault: React.FC = () => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    loadBills();
  }, [search, categoryFilter, tagFilter]);

  const loadBills = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      if (tagFilter) params.tag = tagFilter;
      const billsData = await gmailExpensesService.getBills(params);
      console.log('Bills data received:', billsData);
      setBills(billsData);
    } catch (err: any) {
      console.error('Failed to load bills:', err);
      console.error('Error details:', err.response?.data || err.message);
      // Set empty array to show "no bills" message
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (bill: Bill) => {
    setSelectedBill(bill);
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedBill) return;
    try {
      await gmailExpensesService.updateBill(selectedBill.id, {
        tags: selectedBill.tags,
        notes: selectedBill.notes || '',
      });
      setEditDialogOpen(false);
      loadBills();
    } catch (err) {
      console.error('Failed to update bill:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this bill?')) return;
    try {
      await gmailExpensesService.deleteBill(id);
      loadBills();
    } catch (err) {
      console.error('Failed to delete bill:', err);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      FOOD_DINING: 'primary',
      GROCERIES: 'success',
      FUEL_TRANSPORT: 'warning',
      UTILITIES: 'info',
      MEDICAL: 'error',
      TRAVEL: 'secondary',
    };
    return colors[category] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Typography variant="h4" fontWeight={700}>
          Bill Vault
        </Typography>

        {/* Filters */}
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search bills..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Category"
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    <MenuItem value="FOOD_DINING">Food & Dining</MenuItem>
                    <MenuItem value="GROCERIES">Groceries</MenuItem>
                    <MenuItem value="UTILITIES">Utilities</MenuItem>
                    <MenuItem value="TRAVEL">Travel</MenuItem>
                    <MenuItem value="MEDICAL">Medical</MenuItem>
                    <MenuItem value="BUSINESS_EXPENSES">Business Expenses</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tag</InputLabel>
                  <Select
                    value={tagFilter}
                    label="Tag"
                    onChange={(e) => setTagFilter(e.target.value)}
                  >
                    <MenuItem value="">All Tags</MenuItem>
                    <MenuItem value="PERSONAL">Personal</MenuItem>
                    <MenuItem value="BUSINESS">Business</MenuItem>
                    <MenuItem value="REIMBURSABLE">Reimbursable</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Bills Grid */}
        <Grid container spacing={3}>
          {bills.length === 0 ? (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    No bills found. Connect your Gmail to start tracking bills automatically.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            bills.map((bill) => (
              <Grid item xs={12} sm={6} md={4} key={bill.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {bill.merchant_name}
                      </Typography>
                      <Box>
                        <IconButton size="small" onClick={() => handleEdit(bill)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(bill.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {new Date(bill.bill_date).toLocaleDateString()}
                    </Typography>

                    <Typography variant="h5" fontWeight={700} sx={{ my: 1 }}>
                      {bill.currency} {bill.amount.toFixed(2)}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                      <Chip
                        label={bill.category.replace('_', ' ')}
                        size="small"
                        color={getCategoryColor(bill.category) as any}
                      />
                      {bill.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>

                    {bill.file_url && (
                      <Button
                        startIcon={<PdfIcon />}
                        size="small"
                        sx={{ mt: 2 }}
                        href={bill.file_url}
                        target="_blank"
                      >
                        View Bill
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Bill</DialogTitle>
          <DialogContent>
            {selectedBill && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Notes"
                  multiline
                  rows={4}
                  fullWidth
                  value={selectedBill.notes || ''}
                  onChange={(e) =>
                    setSelectedBill({ ...selectedBill, notes: e.target.value })
                  }
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Container>
  );
};

export default BillVault;
